/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   ใบสั่งงานการผลิต (Production Work Order)

   คอลัมน์ตามแบบฟอร์มที่ลูกค้าส่งมา
     JOB order / Production order / จำนวน batch / FG code / FG name /
     จุดพิมพ์รหัส / รูปแบบอิงค์ / shelf life / บรรทัดที่ 1-3 /
     Lot code / batch code / PK code / PK name / จำนวนเบิก / ระบบการผลิต

   1 สินค้า = 1 แถวหลัก ถ้ามีบรรจุภัณฑ์หลายรายการจะแตกแถวย่อยตาม PK
   พิมพ์เป็น A4 แนวนอน และดาวน์โหลดเป็น Excel ได้
============================================================ */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Modal, Button } from "antd";
import dayjs from "dayjs";
import { PiPrinterFill, PiMicrosoftExcelLogoFill } from "react-icons/pi";

import logo from "../../../assets/images/logo_nsf.png";
import ProductionOrderService from "../../../service/ERP/ProductionOrder.service";
import "./pd-work-order.css";

const pdService = ProductionOrderService();
const PAGE_STYLE_ID = "pd-wo-print-page";

const COLUMNS = [
  { key: "jocode", title: "JOB order", width: "5%" },
  { key: "pdcode", title: "Production order", width: "6%" },
  { key: "batch_count", title: "จำนวน batch", width: "4%", align: "c" },
  { key: "fg_code", title: "FG code", width: "6%" },
  { key: "fg_name", title: "FG name", width: "11%" },
  { key: "print_point", title: "จุดพิมพ์รหัส", width: "6%" },
  { key: "ink_name", title: "รูปแบบอิงค์", width: "6%" },
  { key: "shelf_life", title: "shelf life", width: "4%", align: "c" },
  { key: "line_1", title: "บรรทัดที่ 1", width: "7%" },
  { key: "lot_code", title: "Lot code", width: "4%", align: "c" },
  { key: "batch_code", title: "batch code", width: "5%", align: "c" },
  { key: "line_2", title: "บรรทัดที่ 2", width: "7%" },
  { key: "line_3", title: "บรรทัดที่ 3", width: "7%" },
  { key: "pk_code", title: "PK code", width: "6%" },
  { key: "pk_name", title: "PK name", width: "10%" },
  { key: "withdraw_qty", title: "จำนวนเบิก", width: "5%", align: "r" },
  { key: "production_system", title: "ระบบการผลิต", width: "6%" },
];

const qnum = (v) =>
  v === 0 || v
    ? Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 })
    : "";

/* แตกข้อมูลเป็นแถวตาราง — 1 แถวต่อ 1 บรรจุภัณฑ์ (ไม่มีก็ 1 แถว) */
const toRows = (rows = []) =>
  rows.flatMap((r) => {
    const batchCodes = (r.batches || []).map((b) => b.batch_code).join(", ");
    const base = {
      jocode: r.jocode || "",
      pdcode: r.pdcode || "",
      batch_count: r.batch_count || "",
      fg_code: r.fg_code || "",
      fg_name: r.fg_name || "",
      print_point: r.print_point || "",
      ink_name: r.ink_name || "",
      shelf_life: r.shelf_life || "",
      line_1: r.line_1 || "",
      lot_code: r.lot_code || "",
      batch_code: batchCodes,
      line_2: r.line_2 || "",
      line_3: r.line_3 || "",
      production_system: r.production_system || "",
    };
    const pks = r.packings || [];
    if (pks.length === 0) {
      return [{ ...base, pk_code: "", pk_name: "", withdraw_qty: "" }];
    }
    return pks.map((p, i) => ({
      ...base,
      /* ข้อมูลหัวรายการแสดงเฉพาะแถวแรกของสินค้านั้น */
      ...(i === 0
        ? {}
        : Object.fromEntries(Object.keys(base).map((k) => [k, ""]))),
      pk_code: p.pk_code || "",
      pk_name: p.pk_name || "",
      withdraw_qty: p.withdraw_qty,
    }));
  });

export default function PdWorkOrderPreview() {
  const { code } = useParams();
  const [header, setHeader] = useState(null);
  const [rows, setRows] = useState([]);

  const tableRows = useMemo(() => toRows(rows), [rows]);

  const handlePrint = () => window.print();

  /* ดาวน์โหลดเป็นไฟล์ Excel — สร้างจากตาราง HTML จึงไม่ต้องใช้ไลบรารีเพิ่ม */
  const handleExcel = () => {
    const head = COLUMNS.map((c) => `<th>${c.title}</th>`).join("");
    const body = tableRows
      .map(
        (r) =>
          "<tr>" +
          COLUMNS.map(
            (c) =>
              `<td>${
                c.key === "withdraw_qty" ? qnum(r[c.key]) : r[c.key] ?? ""
              }</td>`
          ).join("") +
          "</tr>"
      )
      .join("");

    const html = `<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head>
      <meta charset="utf-8" /></head><body>
      <table border="1"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
      </body></html>`;

    const blob = new Blob(["﻿", html], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ใบสั่งงานการผลิต_${code}.xls`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    let style = document.getElementById(PAGE_STYLE_ID);
    if (!style) {
      style = document.createElement("style");
      style.id = PAGE_STYLE_ID;
      style.media = "print";
      style.innerHTML = "@page { size: A4 landscape; margin: 6mm; }";
      document.head.appendChild(style);
    }
    return () => {
      const s = document.getElementById(PAGE_STYLE_ID);
      if (s) s.remove();
    };
  }, []);

  useEffect(() => {
    pdService
      .workOrder(code)
      .then((res) => {
        setHeader(res.data?.data?.header || null);
        setRows(res.data?.data?.rows || []);
      })
      .catch((err) => {
        Modal.error({
          title: "Error",
          content: err.response?.data?.message || "โหลดข้อมูลไม่สำเร็จ",
          onOk: () => window.close(),
        });
      });
  }, [code]);

  return (
    <div className="page-show" id="pd-wo">
      <div className="doc-wrapper">
        <div className="print-bar no-print">
          <Button
            className="bn-center"
            onClick={handleExcel}
            icon={<PiMicrosoftExcelLogoFill style={{ fontSize: "1.1rem" }} />}
          >
            EXCEL
          </Button>
          <Button
            className="bn-center bg-blue-400"
            onClick={handlePrint}
            icon={<PiPrinterFill style={{ fontSize: "1.1rem" }} />}
          >
            PRINT
          </Button>
        </div>

        <section className="print-page">
          <div className="wo-head">
            <img src={logo} alt="" className="wo-logo" />
            <div>
              <p className="wo-company">บริษัทไนน์สตาร์ฟู้ด จำกัด</p>
              <p className="wo-doc">ใบสั่งงานการผลิต</p>
            </div>
            <div className="wo-meta">
              <div>
                เลขที่ใบสั่งผลิต : <b>{header?.pdcode || code}</b>
              </div>
              <div>
                วันที่ :{" "}
                <b>
                  {header?.pddate
                    ? dayjs(header.pddate).format("DD/MM/YYYY")
                    : ""}
                </b>
              </div>
            </div>
          </div>

          <table className="wo-table">
            <colgroup>
              {COLUMNS.map((c) => (
                <col key={c.key} style={{ width: c.width }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th key={c.key}>{c.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((r, i) => (
                <tr key={i}>
                  {COLUMNS.map((c) => (
                    <td key={c.key} className={c.align || ""}>
                      {c.key === "withdraw_qty" ? qnum(r[c.key]) : r[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
}
